import { useState, useEffect, useRef, useCallback } from "react";
import { 
  MessageCircle, X, Send, ChevronLeft, Search, 
  Users, User, Circle, Check, CheckCheck, Volume2, VolumeX,
  Bell, AlertTriangle, Clock, ChevronRight
} from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Chat sound effect - Local asset for reliability (no CDN dependency)
const CHAT_SOUND = "/sounds/notification.mp3";

const ChatBubble = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [view, setView] = useState("conversations"); // conversations, chat, new
  const [conversations, setConversations] = useState([]);
  const [activeConversation, setActiveConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState("");
  const [unreadCount, setUnreadCount] = useState(0);
  const [staffList, setStaffList] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [loading, setLoading] = useState(false);
  const [soundEnabled, setSoundEnabled] = useState(true);
  const messagesEndRef = useRef(null);
  const pollIntervalRef = useRef(null);
  const notificationAudioRef = useRef(null);
  const sentAudioRef = useRef(null);
  const lastMessageCountRef = useRef(0);
  const prevUnreadCountRef = useRef(0);
  const isOpenRef = useRef(false); // Track open state for sound logic
  const [taskReminders, setTaskReminders] = useState([]);
  const [showReminders, setShowReminders] = useState(false);
  const reminderIntervalRef = useRef(null);
  const dismissedRemindersRef = useRef(new Set());

  // Keep isOpenRef in sync with isOpen state
  useEffect(() => {
    isOpenRef.current = isOpen;
  }, [isOpen]);

  // Initialize and preload audio for instant playback
  useEffect(() => {
    // Notification sound for incoming messages (higher volume)
    notificationAudioRef.current = new Audio(CHAT_SOUND);
    notificationAudioRef.current.volume = 0.5;
    notificationAudioRef.current.preload = "auto";
    
    // Sent message sound (lower volume)
    sentAudioRef.current = new Audio(CHAT_SOUND);
    sentAudioRef.current.volume = 0.25;
    sentAudioRef.current.preload = "auto";
    
    // Force preload by loading the audio
    notificationAudioRef.current.load();
    sentAudioRef.current.load();
    
    // Load sound preference from localStorage
    const savedPref = localStorage.getItem("chat_sound_enabled");
    if (savedPref !== null) {
      setSoundEnabled(savedPref === "true");
    }
    
    // Load dismissed reminders
    const dismissed = localStorage.getItem("dismissed_reminders");
    if (dismissed) {
      try {
        dismissedRemindersRef.current = new Set(JSON.parse(dismissed));
      } catch (e) {
        // Ignore parse errors for dismissed reminders
      }
    }
  }, []);

  // Play notification sound only when user is NOT actively viewing the chat
  // Sound should play when:
  // - Chat panel is CLOSED (user can't see messages)
  // - OR Chat is open BUT tab is hidden (user switched to another tab)
  // Sound should NOT play when:
  // - Chat panel is OPEN AND tab is visible (user is actively reading messages)
  const playNotificationSound = useCallback(() => {
    // Only play if sound is enabled
    if (!soundEnabled || !notificationAudioRef.current) return;
    
    // Don't play sound if chat is open AND user is actively on this tab
    const isActivelyViewingChat = isOpenRef.current && !document.hidden;
    
    if (!isActivelyViewingChat) {
      // Clone the audio for instant playback (no delay from resetting currentTime)
      const sound = notificationAudioRef.current.cloneNode();
      sound.volume = 0.5;
      sound.play().catch(err => {
        
      });
    }
  }, [soundEnabled]);

  // Play sent message sound (low volume confirmation)
  const playSentMessageSound = useCallback(() => {
    if (!soundEnabled || !sentAudioRef.current) return;
    
    // Clone for instant playback
    const sound = sentAudioRef.current.cloneNode();
    sound.volume = 0.25;
    sound.play().catch(err => {
      
    });
  }, [soundEnabled]);

  // Toggle sound
  const toggleSound = () => {
    const newValue = !soundEnabled;
    setSoundEnabled(newValue);
    localStorage.setItem("chat_sound_enabled", String(newValue));
  };

  const getToken = () => localStorage.getItem("admin_token");
  const getCurrentUser = () => {
    try {
      return JSON.parse(localStorage.getItem("admin_user") || "{}");
    } catch {
      return {};
    }
  };

  // Fetch task reminders
  const fetchTaskReminders = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/tasks/reminders/pending`, {
        headers: { Authorization: `Bearer ${getToken()}` }
      });
      if (res.ok) {
        const data = await res.json();
        // Filter out dismissed reminders
        const activeReminders = data.reminders.filter(
          r => !dismissedRemindersRef.current.has(r.id)
        );
        
        // Check for new reminders and play sound
        if (activeReminders.length > taskReminders.length && soundEnabled) {
          playNotificationSound();
        }
        
        setTaskReminders(activeReminders);
        
        // Auto-show reminders if there are overdue tasks
        if (activeReminders.some(r => r.type === "overdue") && !isOpen) {
          setShowReminders(true);
        }
      }
    } catch (error) {
      console.error("Failed to fetch reminders:", error);
    }
  }, [taskReminders.length, soundEnabled, isOpen, playNotificationSound]);

  // Dismiss a reminder
  const dismissReminder = (reminderId) => {
    dismissedRemindersRef.current.add(reminderId);
    localStorage.setItem("dismissed_reminders", JSON.stringify([...dismissedRemindersRef.current]));
    setTaskReminders(prev => prev.filter(r => r.id !== reminderId));
  };

  // Clear all dismissed reminders (reset daily)
  useEffect(() => {
    const lastReset = localStorage.getItem("reminders_last_reset");
    const today = new Date().toDateString();
    if (lastReset !== today) {
      dismissedRemindersRef.current.clear();
      localStorage.setItem("dismissed_reminders", "[]");
      localStorage.setItem("reminders_last_reset", today);
    }
  }, []);

  // Fetch conversations
  const fetchConversations = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/chat/conversations`, {
        headers: { Authorization: `Bearer ${getToken()}` }
      });
      if (res.ok) {
        const data = await res.json();
        setConversations(data);
      }
    } catch (error) {
      console.error("Failed to fetch conversations:", error);
    }
  }, []);

  // Fetch unread count
  const fetchUnreadCount = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/chat/unread-count`, {
        headers: { Authorization: `Bearer ${getToken()}` }
      });
      if (res.ok) {
        const data = await res.json();
        const newUnreadCount = data.unread_count;
        
        // Play sound if unread count increased (new message arrived)
        // BUT only if the chat is closed or tab is hidden
        if (newUnreadCount > prevUnreadCountRef.current && prevUnreadCountRef.current >= 0) {
          // Double-check: don't play sound if user is actively viewing chat
          const isActivelyViewingChat = isOpenRef.current && !document.hidden;
          if (!isActivelyViewingChat) {
            playNotificationSound();
          }
        }
        
        prevUnreadCountRef.current = newUnreadCount;
        setUnreadCount(newUnreadCount);
      }
    } catch (error) {
      console.error("Failed to fetch unread count:", error);
    }
  }, [playNotificationSound]);

  // Fetch messages for active conversation
  // Note: No sound here - we're actively viewing, sound is handled by unread count
  const fetchMessages = useCallback(async (convId) => {
    if (!convId) return;
    try {
      const res = await fetch(`${API_URL}/api/chat/conversations/${convId}/messages`, {
        headers: { Authorization: `Bearer ${getToken()}` }
      });
      if (res.ok) {
        const data = await res.json();
        lastMessageCountRef.current = data.length;
        setMessages(data);
      }
    } catch (error) {
      console.error("Failed to fetch messages:", error);
    }
  }, []);

  // Fetch staff list
  const fetchStaffList = async () => {
    try {
      const res = await fetch(`${API_URL}/api/chat/staff`, {
        headers: { Authorization: `Bearer ${getToken()}` }
      });
      if (res.ok) {
        const data = await res.json();
        setStaffList(data);
      }
    } catch (error) {
      console.error("Failed to fetch staff:", error);
    }
  };

  // Initial load and polling
  useEffect(() => {
    fetchUnreadCount();
    fetchTaskReminders(); // Fetch reminders on load
    
    // Poll for unread count every 30 seconds
    const unreadInterval = setInterval(fetchUnreadCount, 30000);
    
    // Poll for task reminders every 5 minutes
    reminderIntervalRef.current = setInterval(fetchTaskReminders, 5 * 60 * 1000);
    
    return () => {
      clearInterval(unreadInterval);
      if (reminderIntervalRef.current) {
        clearInterval(reminderIntervalRef.current);
      }
    };
  }, [fetchUnreadCount, fetchTaskReminders]);

  // Poll for messages when chat is open
  useEffect(() => {
    if (isOpen && activeConversation) {
      // Initial fetch
      fetchMessages(activeConversation.id);
      
      // Refresh unread count (messages are now marked as read on server)
      setTimeout(() => fetchUnreadCount(), 500);
      
      // Poll every 5 seconds
      pollIntervalRef.current = setInterval(() => {
        fetchMessages(activeConversation.id);
        // Also refresh unread count to keep badge in sync
        fetchUnreadCount();
      }, 5000);
      
      return () => {
        if (pollIntervalRef.current) {
          clearInterval(pollIntervalRef.current);
        }
      };
    }
  }, [isOpen, activeConversation, fetchMessages, fetchUnreadCount]);

  // Load conversations when panel opens
  useEffect(() => {
    if (isOpen) {
      fetchConversations();
    }
  }, [isOpen, fetchConversations]);

  // Scroll to bottom when messages change
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  // Send message
  const sendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim() || !activeConversation) return;

    const content = newMessage.trim();
    setNewMessage("");

    try {
      const res = await fetch(`${API_URL}/api/chat/conversations/${activeConversation.id}/messages`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${getToken()}`
        },
        body: JSON.stringify({ content })
      });
      
      if (res.ok) {
        const message = await res.json();
        setMessages(prev => [...prev, message]);
        fetchConversations(); // Update last message
        
        // Play sent message sound (low volume confirmation)
        playSentMessageSound();
      }
    } catch (error) {
      console.error("Failed to send message:", error);
    }
  };

  // Start new conversation
  const startConversation = async (staffMember) => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/chat/conversations`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${getToken()}`
        },
        body: JSON.stringify({
          participant_ids: [staffMember.id],
          is_group: false
        })
      });
      
      if (res.ok) {
        const data = await res.json();
        setActiveConversation(data.conversation);
        setView("chat");
        fetchConversations();
      }
    } catch (error) {
      console.error("Failed to create conversation:", error);
    } finally {
      setLoading(false);
    }
  };

  // Get conversation display name
  const getConversationName = (conv) => {
    if (conv.is_group) return conv.group_name;
    const currentUser = getCurrentUser();
    const otherParticipant = conv.participants.find(p => p.user_id !== currentUser.id);
    return otherParticipant?.user_name || "Unknown";
  };

  // Format time
  const formatTime = (dateStr) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return "now";
    if (diffMins < 60) return `${diffMins}m`;
    if (diffHours < 24) return `${diffHours}h`;
    if (diffDays < 7) return `${diffDays}d`;
    return date.toLocaleDateString();
  };

  const currentUser = getCurrentUser();
  const filteredStaff = staffList.filter(s => 
    s.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    s.email.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <>
      {/* Chat Bubble Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={`fixed bottom-6 right-6 w-14 h-14 rounded-full shadow-lg flex items-center justify-center transition-all z-50 ${
          isOpen ? "bg-stone-700 rotate-90" : "bg-primary hover:bg-primary/90 hover:scale-105"
        }`}
        data-testid="chat-bubble-btn"
      >
        {isOpen ? (
          <X className="w-6 h-6 text-white" />
        ) : (
          <>
            <MessageCircle className="w-6 h-6 text-white" />
            {unreadCount > 0 && (
              <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white text-xs font-bold rounded-full flex items-center justify-center">
                {unreadCount > 9 ? "9+" : unreadCount}
              </span>
            )}
          </>
        )}
      </button>

      {/* Chat Panel */}
      {isOpen && (
        <div 
          className="fixed bottom-24 right-4 sm:right-6 w-[calc(100vw-32px)] sm:w-[380px] max-w-[380px] h-[520px] bg-white rounded-2xl shadow-2xl border border-stone-200 flex flex-col overflow-hidden z-50 animate-slide-up"
          data-testid="chat-panel"
        >
          {/* Header */}
          <div className="bg-primary px-4 py-3 flex items-center gap-3">
            {view !== "conversations" && (
              <button 
                onClick={() => {
                  setView("conversations");
                  setActiveConversation(null);
                  lastMessageCountRef.current = 0;
                }}
                className="p-1 hover:bg-white/20 rounded-lg transition-colors"
              >
                <ChevronLeft className="w-5 h-5 text-white" />
              </button>
            )}
            <div className="flex-1">
              <h3 className="text-white font-semibold text-sm">
                {view === "conversations" && "Team Chat"}
                {view === "new" && "New Message"}
                {view === "chat" && activeConversation && getConversationName(activeConversation)}
              </h3>
              {view === "conversations" && (
                <p className="text-white/70 text-xs">Internal messaging</p>
              )}
            </div>
            
            {/* Sound Toggle */}
            <button 
              onClick={toggleSound}
              className="p-2 hover:bg-white/20 rounded-lg transition-colors"
              title={soundEnabled ? "Mute notifications" : "Enable sound"}
              data-testid="sound-toggle-btn"
            >
              {soundEnabled ? (
                <Volume2 className="w-4 h-4 text-white" />
              ) : (
                <VolumeX className="w-4 h-4 text-white/50" />
              )}
            </button>
            
            {view === "conversations" && (
              <button 
                onClick={() => {
                  setView("new");
                  fetchStaffList();
                }}
                className="p-2 hover:bg-white/20 rounded-lg transition-colors"
                title="New message"
              >
                <Users className="w-5 h-5 text-white" />
              </button>
            )}
          </div>

          {/* Content Area */}
          <div className="flex-1 overflow-hidden flex flex-col">
            
            {/* Conversations List */}
            {view === "conversations" && (
              <div className="flex-1 overflow-y-auto">
                {conversations.length === 0 ? (
                  <div className="flex flex-col items-center justify-center h-full p-6 text-center">
                    <div className="w-16 h-16 bg-stone-100 rounded-full flex items-center justify-center mb-4">
                      <MessageCircle className="w-8 h-8 text-stone-400" />
                    </div>
                    <p className="text-stone-600 font-medium mb-1">No conversations yet</p>
                    <p className="text-stone-400 text-sm mb-4">Start chatting with your team</p>
                    <button
                      onClick={() => {
                        setView("new");
                        fetchStaffList();
                      }}
                      className="px-4 py-2 bg-primary text-white rounded-lg text-sm font-medium hover:bg-primary/90 transition-colors"
                    >
                      Start a conversation
                    </button>
                  </div>
                ) : (
                  conversations.map((conv) => (
                    <button
                      key={conv.id}
                      onClick={() => {
                        setActiveConversation(conv);
                        setView("chat");
                      }}
                      className="w-full flex items-center gap-3 px-4 py-3 hover:bg-stone-50 transition-colors border-b border-stone-100"
                      data-testid={`conversation-${conv.id}`}
                    >
                      <div className="relative">
                        <div className="w-10 h-10 bg-gradient-to-br from-primary/20 to-primary/10 rounded-full flex items-center justify-center">
                          {conv.is_group ? (
                            <Users className="w-5 h-5 text-primary" />
                          ) : (
                            <span className="text-primary font-bold">
                              {getConversationName(conv).charAt(0)}
                            </span>
                          )}
                        </div>
                        <span className="absolute bottom-0 right-0 w-3 h-3 bg-green-500 rounded-full border-2 border-white" />
                      </div>
                      <div className="flex-1 min-w-0 text-left">
                        <div className="flex items-center justify-between">
                          <p className="font-medium text-stone-800 text-sm truncate">
                            {getConversationName(conv)}
                          </p>
                          {conv.last_message && (
                            <span className="text-xs text-stone-400">
                              {formatTime(conv.last_message.created_at)}
                            </span>
                          )}
                        </div>
                        {conv.last_message && (
                          <p className="text-xs text-stone-500 truncate">
                            {conv.last_message.sender_name === currentUser.name ? "You: " : ""}
                            {conv.last_message.content}
                          </p>
                        )}
                      </div>
                      {conv.unread_count > 0 && (
                        <span className="w-5 h-5 bg-primary text-white text-xs font-bold rounded-full flex items-center justify-center">
                          {conv.unread_count}
                        </span>
                      )}
                    </button>
                  ))
                )}
              </div>
            )}

            {/* New Conversation - Staff List */}
            {view === "new" && (
              <div className="flex-1 overflow-hidden flex flex-col">
                <div className="p-3 border-b border-stone-100">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400" />
                    <input
                      type="text"
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      placeholder="Search team members..."
                      className="w-full pl-9 pr-4 py-2 bg-stone-100 border-none rounded-lg text-sm focus:ring-2 focus:ring-primary/20"
                      data-testid="staff-search-input"
                    />
                  </div>
                </div>
                <div className="flex-1 overflow-y-auto">
                  {filteredStaff.map((staff) => (
                    <button
                      key={staff.id}
                      onClick={() => startConversation(staff)}
                      disabled={loading}
                      className="w-full flex items-center gap-3 px-4 py-3 hover:bg-stone-50 transition-colors border-b border-stone-100 disabled:opacity-50"
                      data-testid={`staff-${staff.id}`}
                    >
                      <div className="w-10 h-10 bg-gradient-to-br from-blue-100 to-blue-50 rounded-full flex items-center justify-center">
                        <span className="text-blue-600 font-bold">
                          {staff.name.charAt(0)}
                        </span>
                      </div>
                      <div className="flex-1 text-left">
                        <p className="font-medium text-stone-800 text-sm">{staff.name}</p>
                        <p className="text-xs text-stone-400">{staff.role}</p>
                      </div>
                    </button>
                  ))}
                  {filteredStaff.length === 0 && (
                    <div className="p-6 text-center text-stone-400 text-sm">
                      No team members found
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Chat Messages */}
            {view === "chat" && activeConversation && (
              <>
                <div className="flex-1 overflow-y-auto p-4 space-y-3">
                  {messages.map((msg) => {
                    const isOwn = msg.sender_id === currentUser.id;
                    return (
                      <div
                        key={msg.id}
                        className={`flex ${isOwn ? "justify-end" : "justify-start"}`}
                      >
                        <div className={`max-w-[75%] ${isOwn ? "order-2" : ""}`}>
                          {!isOwn && (
                            <p className="text-xs text-stone-400 mb-1 ml-1">
                              {msg.sender_name}
                            </p>
                          )}
                          <div
                            className={`px-3 py-2 rounded-2xl ${
                              isOwn
                                ? "bg-primary text-white rounded-br-md"
                                : "bg-stone-100 text-stone-800 rounded-bl-md"
                            }`}
                          >
                            <p className="text-sm whitespace-pre-wrap break-words">
                              {msg.content}
                            </p>
                          </div>
                          <div className={`flex items-center gap-1 mt-1 ${isOwn ? "justify-end" : ""}`}>
                            <span className="text-[10px] text-stone-400">
                              {formatTime(msg.created_at)}
                            </span>
                            {isOwn && (
                              msg.is_read ? (
                                <CheckCheck className="w-3 h-3 text-blue-500" />
                              ) : (
                                <Check className="w-3 h-3 text-stone-400" />
                              )
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                  <div ref={messagesEndRef} />
                </div>

                {/* Message Input */}
                <form onSubmit={sendMessage} className="p-3 border-t border-stone-100">
                  <div className="flex items-center gap-2">
                    <input
                      type="text"
                      value={newMessage}
                      onChange={(e) => setNewMessage(e.target.value)}
                      placeholder="Type a message..."
                      className="flex-1 px-4 py-2 bg-stone-100 border-none rounded-full text-sm focus:ring-2 focus:ring-primary/20"
                      data-testid="message-input"
                    />
                    <button
                      type="submit"
                      disabled={!newMessage.trim()}
                      className="w-10 h-10 bg-primary text-white rounded-full flex items-center justify-center hover:bg-primary/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      data-testid="send-message-btn"
                    >
                      <Send className="w-4 h-4" />
                    </button>
                  </div>
                </form>
              </>
            )}
          </div>
        </div>
      )}

      {/* Task Reminders Popup */}
      {showReminders && taskReminders.length > 0 && !isOpen && (
        <div 
          className="fixed bottom-24 right-6 w-80 bg-white rounded-xl shadow-2xl border border-stone-200 overflow-hidden animate-slide-up z-50"
          data-testid="task-reminders-popup"
        >
          <div className="bg-gradient-to-r from-amber-500 to-orange-500 p-3 flex items-center justify-between">
            <div className="flex items-center gap-2 text-white">
              <Bell className="w-4 h-4" />
              <span className="font-semibold text-sm">Task Reminders</span>
              <span className="bg-white/20 px-2 py-0.5 rounded-full text-xs">
                {taskReminders.length}
              </span>
            </div>
            <button
              onClick={() => setShowReminders(false)}
              className="text-white/80 hover:text-white transition-colors"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
          
          <div className="max-h-64 overflow-y-auto">
            {taskReminders.map((reminder) => (
              <div
                key={reminder.id}
                className={`p-3 border-b border-stone-100 last:border-b-0 hover:bg-stone-50 transition-colors ${
                  reminder.type === "overdue" ? "bg-red-50/50" : ""
                }`}
              >
                <div className="flex items-start gap-2">
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                    reminder.type === "overdue" ? "bg-red-100 text-red-600" :
                    reminder.type === "due_today" ? "bg-amber-100 text-amber-600" :
                    "bg-orange-100 text-orange-600"
                  }`}>
                    {reminder.type === "overdue" ? (
                      <AlertTriangle className="w-4 h-4" />
                    ) : (
                      <Clock className="w-4 h-4" />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-stone-800 truncate">
                      {reminder.title}
                    </p>
                    <p className={`text-xs mt-0.5 ${
                      reminder.type === "overdue" ? "text-red-600" : "text-stone-500"
                    }`}>
                      {reminder.type === "overdue" && "⚠️ Overdue"}
                      {reminder.type === "due_today" && "📅 Due today"}
                      {reminder.type === "high_priority" && "🔴 High priority"}
                      {reminder.due_date && ` • ${reminder.due_date.slice(0, 10)}`}
                    </p>
                  </div>
                  <button
                    onClick={() => dismissReminder(reminder.id)}
                    className="text-stone-400 hover:text-stone-600 p-1"
                    title="Dismiss"
                  >
                    <X className="w-3 h-3" />
                  </button>
                </div>
              </div>
            ))}
          </div>
          
          <div className="p-2 bg-stone-50 border-t border-stone-100">
            <button
              onClick={() => {
                setShowReminders(false);
                window.location.href = "/admin/tasks";
              }}
              className="w-full flex items-center justify-center gap-2 px-3 py-2 text-sm text-primary hover:bg-primary/10 rounded-lg transition-colors"
            >
              View All Tasks
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}

      {/* Reminder Badge on Chat Bubble */}
      {!isOpen && taskReminders.length > 0 && (
        <button
          onClick={() => setShowReminders(!showReminders)}
          className="fixed bottom-24 right-20 w-10 h-10 bg-amber-500 text-white rounded-full shadow-lg flex items-center justify-center hover:bg-amber-600 transition-colors z-40 animate-bounce"
          data-testid="reminder-badge"
          title={`${taskReminders.length} task reminder(s)`}
        >
          <Bell className="w-5 h-5" />
          {taskReminders.length > 0 && (
            <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white text-xs rounded-full flex items-center justify-center font-bold">
              {taskReminders.length}
            </span>
          )}
        </button>
      )}

      <style>{`
        @keyframes slide-up {
          from { opacity: 0; transform: translateY(20px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .animate-slide-up {
          animation: slide-up 0.2s ease-out;
        }
      `}</style>
    </>
  );
};

export default ChatBubble;
